import React, { useMemo } from "react";
import { isNullOrEmpty } from "../../utils/commonUtils";
import { cloneDeep } from "lodash";
import { ChevronDown, ChevronUp, Check, TriangleAlert, X, RotateCcw, Upload } from "lucide-react";
import {
  CommentLoadSection,
  CircleLoader,
} from "../../components/ui/commentLoadSection";
import { Flex, FlexItem } from "../layout/FlexLayout";
import { Grid } from "../layout/GridLayout";
import { StatusBadge } from "../loan-list/column-formatter";
import { Button } from "./button";

export const ERROR_TYPES = {
  FILE_TYPE: "FILE_TYPE",
  FILE_SIZE: "FILE_SIZE",
  UPLOAD_FAILED: "UPLOAD_FAILED",
};

export const FileUploadProgress = ({ info, onRetry, onRemove, isUploadInProgress }) => {
  const { isUploaded, fileName, fileSize, percentage, errorType, id } = info;
  const loaderColors = { trackColor: "#FFF", color: "#DE7538" };
  const isError = info.message === "Uploading failed" || errorType;
  const isUploading = info.message === "Uploading..." && info.isUploaded === null;

  const getErrorMessage = () => {
    if (errorType === ERROR_TYPES.FILE_TYPE) return "File type not supported";
    if (errorType === ERROR_TYPES.FILE_SIZE) return "File size is more than 500 MB";
    return "Error uploading file. Please try again.";
  };

  const renderActionButtons = () => {
    if (errorType === ERROR_TYPES.FILE_TYPE) {
      return (
        <Button
          variant="primaryGhost"
          size="icon"
          onClick={() => onRemove?.(id)}
          disabled={isUploadInProgress}
          className="h-8 w-8 text-neutral-8 hover:text-neutral-12"
        >
          <X className="w-4 h-4 text-red-500" />
        </Button>
      );
    }
    if (errorType === ERROR_TYPES.FILE_SIZE) {
      return (
        <Button
          variant="primaryOutline"
          size="sm"
          onClick={() => onRetry?.(id, true)}
          disabled={isUploadInProgress}
          className="h-8 px-3 gap-1"
        >
          Upload <Upload className="w-4 h-4" />
        </Button>
      );
    }
    // Upload failed - show retry and remove buttons
    return (
      <Flex gap={2} alignItems="center">
        <Button
          variant="primaryOutline"
          size="sm"
          onClick={() => onRetry?.(id)}
          disabled={isUploadInProgress}
          className="h-8 px-3 gap-1"
        >
          Retry <RotateCcw className="w-4 h-4" />
        </Button>
        <Button
          variant="primaryGhost"
          size="icon"
          onClick={() => onRemove?.(id)}
          disabled={isUploadInProgress}
          className="h-8 w-8 text-neutral-8 hover:text-neutral-12"
        >
          <X className="w-4 h-4 text-red-600" />
        </Button>
      </Flex>
    );
  };

  return (
    <FlexItem key={id} className="flex-1">
      <Flex justify="between" alignItems="center" direction="column">
        <FlexItem>
          <div className="text-sm text-neutral-13">{fileName}</div>
          {isError ? (
            <div className="flex items-center gap-1 text-xs text-system-negative-2">
              <TriangleAlert className="w-3 h-3" />
              {getErrorMessage()}
            </div>
          ) : 
          (<div className="text-sm text-neutral-8">
            {fileSize ? (fileSize / 1048576).toFixed(2) : "--"} MB
          </div>)}
        </FlexItem>
        <FlexItem>
          {isUploaded && (
            <StatusBadge
              size="md"
              status={
                <p className="flex gap-2">
                  <Check className="w-5 h-5" /> Uploaded
                </p>
              }
              variant="submitted"
            />
          )}
          {isUploading && (
            <Flex direction="row" items="center" gap={2}>
              <CircleLoader
                percentage={percentage}
                size={20}
                strokeWidth={2}
                trackColor={loaderColors.trackColor}
                color={loaderColors.color}
              />
              <div className="text-sm text-[var(--color-misc-progress-orange-2)]">
                Uploading
              </div>
            </Flex>
          )}
          {isError && renderActionButtons()}
        </FlexItem>
      </Flex>
    </FlexItem>
  );
};

export const createDocumentResponseLocally = (
  files,
  localContext,
  contextKey,
  documentType,
  parentType,
  parentId
) => {
  return files.map((file, index) => {
    return {
      ...emptyValuedDocument,
      index: localContext?.[contextKey]?.length + index,
      name: file.name,
      createdOn: new Date().toISOString(),
      documentType,
      parentType,
      parentId,
    };
  });
};

export const emptyValuedDocument = {
  documentId: null,
  documentType: "Others",
  parentType: "",
  parentId: "",
  name: "",
  path: "",
  review: {
    reason: null,
    reviewStatus: "Pending Review",
  },
  comment: null,
  type: "tag",
  status: "ACTIVE",
  fileSize: 0,
  statementDate: null,
};

export const extensionList = [
  "docs",
  "docx",
  "doc",
  "pdf",
  "jpg",
  "jpeg",
  "png",
  "xlsx",
  "xls",
];

export const assignDocIdAndPath = (
  files,
  localContext,
  contextKey,
  documentType,
  parentType,
  parentId
) => {
  return files.map((file, index) => {
    return {
      ...emptyValuedDocument,
      index: localContext?.[contextKey]?.length + index,
      name: file.name,
      createdOn: file.createdOn,
      documentId: file.documentId,
      path: file.path,
      documentType,
      parentType,
      parentId,
    };
  });
};

export const toUpperCase = (_extensionList) => {
  return _extensionList.map((item) => {
    return item.toUpperCase();
  });
};
const errorMessage = (list) => {
  const uppercaseExtensionList = toUpperCase(list);
  return `Supported file formats: ${uppercaseExtensionList
    .join(", ")
    .replace(/_/g, " ")}.`;
};

const checkDocFormat = (file, overRideFileFormat) => {
  const fileExt = file.name.split(".").pop().toLowerCase();
  const invalidFileFormat =
    (overRideFileFormat || extensionList).indexOf(fileExt) === -1;
  if (invalidFileFormat) {
    return { error: errorMessage(overRideFileFormat || extensionList), errorType: ERROR_TYPES.FILE_TYPE };
  }
  if (file.size > 524288000) {
    return { error: "Max file fileSize: 500MB", errorType: ERROR_TYPES.FILE_SIZE };
  }
  return { error: "", errorType: null };
};

const UPLOADING_FAILED = "Uploading failed";

const updateProgressInfo = (setProgressInfos, id, updates) => {
  setProgressInfos((prev) =>
    prev.map((info) => (info.id === id ? { ...info, ...updates } : info))
  );
};

const createFailedUploadUpdate = (errorType, description = null) => ({
  percentage: 0,
  message: UPLOADING_FAILED,
  errorType,
  isUploaded: false,
  ...(description && { description }),
});

const createSuccessUpdate = () => ({
  message: "Uploaded successfully",
  isUploaded: true,
});

const createProgressUpdate = (event, preSignedUrl) => ({
  percentage: Math.round((100 * event.loaded) / event.total),
  path: preSignedUrl.split("?")[0],
});

const shouldSkipUpload = (fileValidation, skipSizeCheck) => {
  if (isNullOrEmpty(fileValidation.error)) return false;
  if (skipSizeCheck && fileValidation.errorType === ERROR_TYPES.FILE_SIZE) return false;
  return true;
};

const handleUploadToS3 = (uploadToAwsS3, preSignedUrl, file, id, setProgressInfos) => {
  const onProgress = (event) => {
    updateProgressInfo(setProgressInfos, id, createProgressUpdate(event, preSignedUrl));
  };

  return uploadToAwsS3(preSignedUrl, file, onProgress)
    .then(() => {
      updateProgressInfo(setProgressInfos, id, createSuccessUpdate());
    })
    .catch(() => {
      updateProgressInfo(setProgressInfos, id, createFailedUploadUpdate(ERROR_TYPES.UPLOAD_FAILED));
    });
};

export const upload = ({
  id,
  file,
  progressInfos,
  setProgressInfos,
  loanId,
  originatorId = null,
  parentType,
  originatorLoanId = null,
  overRideFileFormat,
  generatePreSignedUrl,
  uploadToAwsS3,
  skipSizeCheck = false,
}) => {
  const fileValidation = checkDocFormat(file, overRideFileFormat);

  if (shouldSkipUpload(fileValidation, skipSizeCheck)) {
    const failedUpdate = createFailedUploadUpdate(fileValidation.errorType, fileValidation.error);
    updateProgressInfo(setProgressInfos, id, failedUpdate);
    return;
  }

  const uploadPath = `${parentType?.toLowerCase()}/${loanId}/${Date.now()}/${file?.name}`;

  generatePreSignedUrl({ path: uploadPath, originatorId, originatorLoanId })
    .then((resp) => {
      const preSignedUrl = resp?.data;
      handleUploadToS3(uploadToAwsS3, preSignedUrl, file, id, setProgressInfos);
    })
    .catch(() => {
      updateProgressInfo(setProgressInfos, id, createFailedUploadUpdate(ERROR_TYPES.UPLOAD_FAILED));
    });
};
export const validateFilesFormat = (docs, allowedExtension, setErrorMsg) => {
  setErrorMsg?.("");
  return false;
};

export const getFileValidationInfo = (file, allowedExtension) => {
  const fileExt = file.name.split(".").pop().toLowerCase();
  const invalidFileFormat = allowedExtension.indexOf(fileExt) === -1;
  if (invalidFileFormat) {
    return { isValid: false, errorType: ERROR_TYPES.FILE_TYPE };
  }
  if (file.size > 524288000) {
    return { isValid: false, errorType: ERROR_TYPES.FILE_SIZE };
  }
  return { isValid: true, errorType: null };
};

const calculateProgressStats = (progressInfos) => {
  let isUploading = false;
  let uploadingCount = 0;
  let errorCount = 0;

  progressInfos.forEach((info) => {
    if (info.isUploaded === null || info.isUploaded === false) {
      if (info.message === "Uploading failed") {
        errorCount++;
      } else {
        isUploading = true;
        uploadingCount++;
      }
    }
  });

  const isError = !isUploading && errorCount > 0;

  return {
    isUploading,
    uploadingCount,
    totalFiles: progressInfos.length,
    isError,
    errorCount,
  };
};

const getLoaderColors = (isError) => {
  if (isError) {
    return { trackColor: "#DA1E28", color: "#DA1E28" };
  }
  return { trackColor: "#DE7538", color: "#FFF" };
};

const ExpandDetailsText = ({ isError }) => (
  <p className="flex gap-2 items-center">
    {isError && <TriangleAlert className="hidden sm:inline text-system-negative-3" />}
    <span className="hidden sm:inline">Collapse Details</span>
    <ChevronUp />
  </p>
);

const CollapsedDetailsText = ({ isError }) => (
  <p className="flex gap-2 items-center">
    {isError && <TriangleAlert className="hidden sm:inline text-system-negative-3" />}
    <span className="hidden sm:inline">Expand Details</span>
    <ChevronDown />
  </p>
);

const UploadingMessage = ({ uploadingCount }) => (
  <>
    <h3 className="text-base sm:text-md font-semibold text-neutral-8">
      {uploadingCount} {uploadingCount > 1 ? "Files" : "File"} are uploading
    </h3>
    <p className="text-sm sm:text-md">
      Please wait while the upload completes. Your documents
      will be automatically sorted under the appropriate
      sections.
    </p>
  </>
);

const SuccessMessage = () => (
  <>
    <h3 className="text-base sm:text-md font-semibold text-neutral-8">
      Files uploaded succesfully
    </h3>
    <p className="text-sm sm:text-md">
      Your documents will be automatically sorted under the
      appropriate sections.
    </p>
  </>
);

const ErrorMessage = ({ errorCount }) => (
  <>
    <h3 className="text-base sm:text-md text-md font-semibold text-neutral-8">
      {errorCount} {errorCount > 1 ? "Errors" : "Error"} while uploading
    </h3>
    <p className="text-sm sm:text-md">
      Some files failed to upload. Please check the details to
      upload the correct ones or try again.
    </p>
  </>
);

const StatusMessage = ({ isUploading, errorCount, uploadingCount }) => {
  if (isUploading || errorCount === 0) {
    return uploadingCount > 0
      ? <UploadingMessage uploadingCount={uploadingCount} />
      : <SuccessMessage />;
  }
  return <ErrorMessage errorCount={errorCount} />;
};

const LoaderSectionHeader = ({ percentageLoad, loaderColors, isUploading, errorCount, uploadingCount }) => (
  <Flex gap="2">
    <FlexItem className="w-14 h-13">
      <CircleLoader
        percentage={percentageLoad}
        size={50}
        strokeWidth={3}
        trackColor={loaderColors.trackColor}
        color={loaderColors.color}
        showFileIcon
      />
    </FlexItem>
    <Flex direction="col" gap="1" items="start">
      <StatusMessage
        isUploading={isUploading}
        errorCount={errorCount}
        uploadingCount={uploadingCount}
      />
    </Flex>
  </Flex>
);

const FileUploadItem = ({ info, onRetry, onRemove, isUploadInProgress }) => (
  <div key={info.id} className="min-h-[40px]">
    <FileUploadProgress
      info={info}
      onRetry={onRetry}
      onRemove={onRemove}
      isUploadInProgress={isUploadInProgress}
    />
  </div>
);

const MobileFileList = ({ progressInfos, onRetry, onRemove, isUploadInProgress }) => (
  <div className="sm:hidden p-8 bg-white rounded-b-2xl flex flex-col gap-6">
    {progressInfos.map((info) => (
      <FileUploadItem
        key={info.id}
        info={info}
        onRetry={onRetry}
        onRemove={onRemove}
        isUploadInProgress={isUploadInProgress}
      />
    ))}
  </div>
);

const SingleFileLayout = ({ info, onRetry, onRemove, isUploadInProgress }) => (
  <div className="hidden sm:block p-8 bg-white rounded-b-2xl">
    <FileUploadProgress
      info={info}
      onRetry={onRetry}
      onRemove={onRemove}
      isUploadInProgress={isUploadInProgress}
    />
  </div>
);

const FileColumn = ({ items, onRetry, onRemove, isUploadInProgress }) => (
  <div className="flex-1 p-8 flex flex-col gap-6">
    {items.map((info) => (
      <FileUploadItem
        key={info.id}
        info={info}
        onRetry={onRetry}
        onRemove={onRemove}
        isUploadInProgress={isUploadInProgress}
      />
    ))}
  </div>
);

const TwoColumnLayout = ({ progressInfos, onRetry, onRemove, isUploadInProgress }) => {
  const leftColumnItems = progressInfos.filter((_, index) => index % 2 === 0);
  const rightColumnItems = progressInfos.filter((_, index) => index % 2 !== 0);

  return (
    <div className="hidden sm:flex flex-row bg-white rounded-b-2xl">
      <FileColumn items={leftColumnItems} onRetry={onRetry} onRemove={onRemove} isUploadInProgress={isUploadInProgress} />
      <div className="w-px bg-[var(--color-neutral-5)]" />
      <FileColumn items={rightColumnItems} onRetry={onRetry} onRemove={onRemove} isUploadInProgress={isUploadInProgress} />
    </div>
  );
};

const DesktopFileList = ({ progressInfos, onRetry, onRemove, isUploadInProgress }) => {
  if (progressInfos.length === 1) {
    return (
      <SingleFileLayout
        info={progressInfos[0]}
        onRetry={onRetry}
        onRemove={onRemove}
        isUploadInProgress={isUploadInProgress}
      />
    );
  }
  return (
    <TwoColumnLayout
      progressInfos={progressInfos}
      onRetry={onRetry}
      onRemove={onRemove}
      isUploadInProgress={isUploadInProgress}
    />
  );
};

export const LoaderSection = ({ progressInfos = [], onRetry, onRemove, isUploadInProgress = false }) => {
  const { isUploading, errorCount, isError, uploadingCount, totalFiles } =
    useMemo(() => calculateProgressStats(progressInfos), [progressInfos]);

  if (progressInfos.length === 0) {
    return null;
  }

  const showErrorState = !isUploading && isError;
  const loaderColors = getLoaderColors(showErrorState);
  const percentageLoad = isUploading ? (100 * uploadingCount) / totalFiles : 100;

  return (
    <div className="pt-4">
      <CommentLoadSection className="rounded-3xl overflow-hidden border border-[var(--color-neutral-5)]">
        <CommentLoadSection.Header isError={isError} className="rounded-t-3xl">
          <Flex alignItems="center" gap="3" justify="between" direction="row">
            <FlexItem>
              <LoaderSectionHeader
                percentageLoad={percentageLoad}
                loaderColors={loaderColors}
                isUploading={isUploading}
                errorCount={errorCount}
                uploadingCount={uploadingCount}
              />
            </FlexItem>
            <Flex items="center">
              <CommentLoadSection.HeaderAction
                expandedText={<ExpandDetailsText isError={showErrorState} />}
                collapsedText={<CollapsedDetailsText isError={showErrorState} />}
                isError={isError}
              />
            </Flex>
          </Flex>
        </CommentLoadSection.Header>
        <CommentLoadSection.Content className="rounded-b-3xl border-t">
          <MobileFileList
            progressInfos={progressInfos}
            onRetry={onRetry}
            onRemove={onRemove}
            isUploadInProgress={isUploadInProgress}
          />
          <DesktopFileList
            progressInfos={progressInfos}
            onRetry={onRetry}
            onRemove={onRemove}
            isUploadInProgress={isUploadInProgress}
          />
        </CommentLoadSection.Content>
      </CommentLoadSection>
    </div>
  );
};
